import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const typeLabels = { kontingent: 'Kontingent', tur: 'Tur/Rejse', arrangement: 'Arrangement' };

export default function Activities() {
  const { apiFetch } = useAuth();
  const [activities, setActivities] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', date: '', end_date: '', amount_per_person: '', activity_type: 'arrangement', fiscal_year_id: '' });
  const [error, setError] = useState('');

  const load = () => {
    Promise.all([
      apiFetch('/api/activities'),
      apiFetch('/api/settings/fiscal-years')
    ]).then(([a, fy]) => {
      setActivities(a);
      setFiscalYears(fy);
      if (fy.length > 0 && !form.fiscal_year_id) {
        const active = fy.find(f => f.is_active);
        setForm(prev => ({ ...prev, fiscal_year_id: active ? active.id : fy[0].id }));
      }
    }).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const openCreateModal = () => {
    setEditingActivity(null);
    setForm(prev => ({ name: '', description: '', date: '', end_date: '', amount_per_person: '', activity_type: 'arrangement', fiscal_year_id: prev.fiscal_year_id }));
    setError('');
    setShowModal(true);
  };

  const openEditModal = (a) => {
    setEditingActivity(a);
    setForm({
      name: a.name,
      description: a.description || '',
      date: a.date ? new Date(a.date).toISOString().slice(0, 10) : '',
      end_date: a.end_date ? new Date(a.end_date).toISOString().slice(0, 10) : '',
      amount_per_person: a.amount_per_person,
      activity_type: a.activity_type,
      fiscal_year_id: a.fiscal_year_id
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingActivity) {
        await apiFetch(`/api/activities/${editingActivity.id}`, { method: 'PUT', body: form });
      } else {
        await apiFetch('/api/activities', { method: 'POST', body: form });
      }
      setShowModal(false);
      setEditingActivity(null);
      setForm(prev => ({ name: '', description: '', date: '', end_date: '', amount_per_person: '', activity_type: 'arrangement', fiscal_year_id: prev.fiscal_year_id }));
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Er du sikker på at du vil slette denne aktivitet og alle tilknyttede deltagere?')) return;
    try {
      await apiFetch(`/api/activities/${id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const fmt = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? '0,00' : n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDateRange = (a) => {
    if (!a.date) return '-';
    const start = new Date(a.date).toLocaleDateString('da-DK');
    if (a.end_date) {
      const end = new Date(a.end_date).toLocaleDateString('da-DK');
      return `${start} – ${end}`;
    }
    return start;
  };

  return (
    <div>
      <div className="page-header">
        <h1>Aktiviteter &amp; Kontingent</h1>
        <button className="btn btn-primary" onClick={openCreateModal}>+ Ny aktivitet</button>
      </div>

      {activities.length === 0 ? (
        <div className="empty-state">
          <p>Ingen aktiviteter oprettet endnu.</p>
          <p>Opret en aktivitet for at styre kontingent, ture eller arrangementer.</p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Navn</th>
                <th>Type</th>
                <th>Dato</th>
                <th className="text-right">Pris/person</th>
                <th className="text-center">Deltagere</th>
                <th className="text-center">Betalt</th>
                <th className="text-right">Indbetalt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {activities.map(a => (
                <tr key={a.id}>
                  <td><Link to={`/app/aktiviteter/${a.id}`} style={{color: 'var(--primary)', fontWeight: 500, textDecoration: 'none'}}>{a.name}</Link></td>
                  <td><span className={`badge ${a.activity_type === 'kontingent' ? 'badge-active' : ''}`}>{typeLabels[a.activity_type] || a.activity_type}</span></td>
                  <td>{formatDateRange(a)}</td>
                  <td className="text-right amount">{fmt(a.amount_per_person)} kr.</td>
                  <td className="text-center">{a.participant_count}</td>
                  <td className="text-center">
                    {a.participant_count > 0 ? (
                      <span style={{color: a.paid_count === a.participant_count ? 'var(--success)' : 'var(--warning)'}}>
                        {a.paid_count}/{a.participant_count}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="text-right amount">{fmt(a.total_paid)} kr.</td>
                  <td className="text-right" style={{whiteSpace: 'nowrap'}}>
                    <button className="btn btn-secondary btn-sm" style={{marginRight: '0.25rem'}} onClick={() => openEditModal(a)}>Rediger</button>
                    <Link to={`/app/aktiviteter/${a.id}`} className="btn btn-primary btn-sm" style={{marginRight: '0.25rem'}}>Administrer</Link>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a.id)}>Slet</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingActivity(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingActivity ? 'Rediger aktivitet' : 'Ny aktivitet'}</h2>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Type</label>
                <select className="form-control" value={form.activity_type}
                  onChange={e => setForm({...form, activity_type: e.target.value})}>
                  <option value="kontingent">Kontingent</option>
                  <option value="tur">Tur/Rejse</option>
                  <option value="arrangement">Arrangement</option>
                </select>
              </div>
              <div className="form-group">
                <label>Navn</label>
                <input className="form-control" value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})} required
                  placeholder={form.activity_type === 'kontingent' ? 'f.eks. Kontingent 2026' : 'f.eks. Tur til Ærø'} />
              </div>
              <div className="form-group">
                <label>Beskrivelse</label>
                <textarea className="form-control" value={form.description}
                  onChange={e => setForm({...form, description: e.target.value})}
                  rows={4}
                  placeholder="Beskriv aktiviteten, praktiske detaljer, tidspunkter mv."
                  style={{resize: 'vertical', minHeight: '80px'}} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Startdato</label>
                  <input className="form-control" type="date" value={form.date}
                    onChange={e => setForm({...form, date: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Slutdato (valgfri)</label>
                  <input className="form-control" type="date" value={form.end_date}
                    onChange={e => setForm({...form, end_date: e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Pris per person (kr.)</label>
                  <input className="form-control" type="number" step="0.01" value={form.amount_per_person}
                    onChange={e => setForm({...form, amount_per_person: e.target.value})} required />
                </div>
                {!editingActivity && (
                  <div className="form-group">
                    <label>Regnskabsår</label>
                    <select className="form-control" value={form.fiscal_year_id}
                      onChange={e => setForm({...form, fiscal_year_id: e.target.value})}>
                      {fiscalYears.map(fy => (
                        <option key={fy.id} value={fy.id}>{fy.name}{fy.is_active ? ' (aktiv)' : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setEditingActivity(null); }}>Annuller</button>
                <button type="submit" className="btn btn-primary">{editingActivity ? 'Gem ændringer' : 'Opret'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
